import _ from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Icon, SingleSelect } from '~/components';
import { useApi, useWorkspace } from '~/contexts';
import { colors, weights } from '~/styles';

const TaskContainer = styled.div`
  display: flex;
  align-items: center;
`;

const Role = styled.div`
  display: flex;
  align-items: center;
  height: 1.125rem;
  margin-left: 0.5rem;
  padding: 0 0.5rem;
  color: ${colors.grey55};
  font-size: 0.625rem;
  font-weight: ${weights.medium};
  background-color: ${colors.grey10};
  border-radius: 999rem;
`;

const BillableIcon = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 1.125rem;
  height: 1.125rem;
  margin-left: 0.5rem;
  color: white;
  font-size: 0.6rem;
  background-color: ${({ isBillable }) => (isBillable ? colors.green : colors.grey40)};
  border-radius: 50%;
`;

function labelForGroup(group, groups) {
  if (group.role && !group.isCompleted) {
    return `${group.role.name} Open Tasks`;
  } else if (group.assignedToMe && !group.isCompleted) {
    if (groups.some((g) => !!g.role && !g.isCompleted)) return 'My Other Open Tasks';
    return 'My Open Tasks';
  } else if (group.assignedToMe && group.isCompleted) {
    return 'My Completed Tasks';
  } else if (!group.assignedToMe && !group.isCompleted) {
    return 'Other Open Tasks';
  } else {
    return 'Other Completed Tasks';
  }
}

export function TaskDisplay({ task, depth = 0 }) {
  if (!task) return null;
  return (
    <TaskContainer style={{ paddingLeft: `${depth * 1.25}rem` }}>
      <div>{task.name}</div>
      {_.map(task.roles, (role) => (
        <Role key={role.role.id}>{role.role.name}</Role>
      ))}
      <BillableIcon isBillable={task.isActuallyBillable}>
        <Icon icon="dollar-sign" data-testid="task_is_billable" />
      </BillableIcon>
    </TaskContainer>
  );
}

const flattenWithAncestors = (groupTasks, allTasks) => {
  const byId = _.keyBy(allTasks, 'id');

  const includeSet = new Set();
  groupTasks.forEach((task) => {
    let current = task;
    while (current && !includeSet.has(current.id)) {
      includeSet.add(current.id);
      current = byId[current.parentId];
    }
  });

  const fullList = [...includeSet].map((id) => byId[id]).filter(Boolean);
  const fullByParent = _.groupBy(fullList, 'parentId');

  const walk = (parentId = null, depth = 0) => {
    return _.sortBy(fullByParent[parentId] || [], 'position').flatMap((task) => [
      { task, depth },
      ...walk(task.id, depth + 1),
    ]);
  };

  return walk();
};

const TaskSelect = React.forwardRef(
  ({ projectId, projectRoleId, memberId, initialTask, value: taskId, ...props }, ref) => {
    const api = useApi();
    const { workspace } = useWorkspace();
    const [isLoading, setIsLoading] = useState(false);
    const [tasks, setTasks] = useState();
    const [filterValue, setFilterValue] = useState('');
    const [filteredTasks, setFilteredTasks] = useState();
    const isMountedRef = useRef(false);

    const task = useMemo(
      () =>
        _.find(tasks, { id: taskId }) ||
        _.find(filteredTasks, { id: taskId }) ||
        (initialTask && _.isEqual(initialTask.id, taskId) ? initialTask : undefined),
      [tasks, taskId, initialTask, filteredTasks],
    );

    const groupedTasks = useMemo(() => {
      const tasksToUse = filterValue && filteredTasks ? filteredTasks : tasks;
      const groups = _.reduce(
        tasksToUse,
        (acc, task) => {
          const { assignedToMe, statusId, roles } = task;
          const isCompleted = statusId === 'completed';
          const role =
            !isCompleted && projectRoleId ? (roles.find(({ role }) => role.id === projectRoleId)?.role ?? null) : null;

          let obj = _.find(acc, { assignedToMe, isCompleted, role });
          if (!obj) {
            obj = { assignedToMe, role, isCompleted, tasks: [] };
            acc.push(obj);
          }

          obj.tasks.push(task);

          return acc;
        },
        [],
      );
      const groupPriority = (group) => {
        if (group.assignedToMe && !group.isCompleted && !group.role) return 1;
        if (group.role && !group.isCompleted) return 2;
        if (!group.assignedToMe && !group.isCompleted) return 3;
        if (group.assignedToMe && group.isCompleted) return 4;
        return 5;
      };

      return _.orderBy(groups, [groupPriority], ['asc']);
    }, [tasks, filteredTasks, filterValue, projectRoleId]);

    useEffect(() => {
      isMountedRef.current = true;
      return () => {
        isMountedRef.current = false;
      };
    }, []);

    useEffect(() => {
      if (!projectId) {
        setTasks();
        return;
      }

      (async () => {
        setIsLoading(true);

        const { data } = await api.www
          .workspaces(workspace.id)
          .timeEntries()
          .projects(projectId)
          .tasks()
          .get({ memberId, projectRoleId, recordStatusId: 'active', limit: 1000 });

        if (!isMountedRef.current) {
          return;
        }

        setIsLoading(false);
        setTasks(data.results);
      })();
    }, [api, workspace.id, projectId, memberId, projectRoleId]);

    useEffect(() => {
      if (!filterValue) {
        return;
      }

      (async () => {
        const { data } = await api.www
          .workspaces(workspace.id)
          .timeEntries()
          .projects(projectId)
          .tasks()
          .get({ memberId, query: filterValue });

        if (!isMountedRef.current) {
          return;
        }
        setFilteredTasks(data.results);
      })();
    }, [api, workspace.id, projectId, memberId, filterValue]);

    return (
      <SingleSelect
        showEmptyOption={true}
        {...props}
        showFilter
        filterPlaceholder="Find tasks"
        noOptionsMessage="No tasks available"
        value={taskId}
        valueRenderer={<TaskDisplay task={task} />}
        disabled={isLoading}
        onFilterChange={(event) => setFilterValue(event.target.value)}
        ref={ref}>
        {groupedTasks?.map((group) => (
          <optgroup
            key={`assigned_${group.assignedToMe}_${group.isCompleted}${group.role ? `_${group.role.id}` : ''}`}
            label={labelForGroup(group, groupedTasks)}>
            {flattenWithAncestors(group.tasks, tasks).map(({ task, depth }) => {
              const disabled =
                task.isArchived ||
                (task.forAssignedOnly &&
                  !task.assignedToMember &&
                  !task.taskRoles.some(({ role }) => role.id === projectRoleId));

              return (
                // eslint-disable-next-line react/no-unknown-property
                <option key={`${group.label}-${task.id}`} tooltip={task.name} value={task.id} disabled={disabled}>
                  <TaskDisplay task={task} disabled={disabled} depth={depth} />
                </option>
              );
            })}
          </optgroup>
        ))}
      </SingleSelect>
    );
  },
);

export default TaskSelect;
